import { Fragment, useEffect, useMemo, useState } from "react";
import { Settings2, Link as LinkIcon } from "lucide-react";
import LotPickerL2 from "@/components/crm/LotPickerL2";
import OptionalLotSection from "@/components/documents/OptionalLotSection";
import ManageMemoryValuesDialog from "@/components/documents/ManageMemoryValuesDialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { J3Select, J3Groups, PANEL } from "@/components/common/ProjectJ3";
import { FileText, MapPin, Calendar as CalendarIcon, ClipboardList, Plus, X, Check, Pencil } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import FileOrScanInput from "@/components/common/FileOrScanInput";
import useUploadQueue from "@/hooks/useUploadQueue";
import { useAuth } from "@/context/AuthContext";
import RenameMemoryValueDialog from "@/components/documents/RenameMemoryValueDialog";
import { useRequiredFields } from "@/lib/useRequiredFields";
const requiredStyle = (bad) => (bad ? { border: "1px solid #e24b4a", boxShadow: "0 0 0 1px #e24b4a" } : {});
import { AlertTriangle } from "lucide-react";

const BLUE = "#2B579A";
const BLUE_SOFT = "#4A8CD8";

/**
 * DocumentCreateDialog — dynamic form built from the selected type's fields.
 *
 * Design specs (applied literally per Hamza's brief):
 *   - Modal bg: linear-gradient(180deg, #232323, #1B1B1B), border #2E2E2E,
 *     radius 16px, shadow 0 20px 60px rgba(0,0,0,0.5)
 *   - Header : 40×40 blue-gradient icon, title + subtitle (selected type)
 *   - Reference pill : blue-tinted with "🔒 Réservée" tag
 *   - Text fields : #1A1A1A / 1.5px border, blue focus
 *   - Section separator : blue-tinted label + fading gradient line
 *
 * File-type dynamic fields are captured client-side (pending File objects
 * kept in `fileFields`) and uploaded AFTER the document has been created,
 * via `POST /api/documents/{id}/upload-field`.
 */
export default function DocumentCreateDialog({ open, onOpenChange, types, projects, onCreated, onProjectCreated }) {
  const [typeId, setTypeId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [crmLink, setCrmLink] = useState("");
  const [saleRows, setSaleRows] = useState([]);
  useEffect(() => { if (open) api.get("/crm/sales", { params: { status: "active" } }).then(({ data: d }) => setSaleRows(d.rows || [])).catch(() => setSaleRows([])); }, [open]);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const isAdminLevel = user?.role === "admin" || user?.is_first_admin;

  // Spec iter76 (2026-02-XX) — bugfix : le formulaire de création utilisait la
  // prop ``types`` de la page Documents, chargée une seule fois au mount.
  // Si un admin modifie un type (nouveau champ, retrait, renommage) dans un
  // autre onglet, la liste locale reste obsolète et les nouveaux documents
  // ratent le champ. On refetch la définition FRAÎCHE des types à chaque
  // ouverture du dialog pour garantir que la version courante s'applique
  // toujours à une nouvelle création — la rétroactivité ne doit jamais
  // faire office de rattrapage silencieux.
  const [freshTypes, setFreshTypes] = useState(types || []);
  const [typesLoading, setTypesLoading] = useState(false);

  // Spec rev81 (2026-08-02) — création inline d'un Siège/Projet directement
  // depuis le formulaire (admins uniquement). État local qui alimente le
  // dropdown immédiatement + `onProjectCreated` callback pour rafraîchir
  // la liste globale du parent.
  const [localProjects, setLocalProjects] = useState([]);
  const [creatingProject, setCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [savingProject, setSavingProject] = useState(false);
  const allProjects = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of [...(projects || []), ...localProjects]) {
      if (!seen.has(p.id)) { seen.add(p.id); out.push(p); }
    }
    return out;
  }, [projects, localProjects]);

  // Shared upload queue for every dynamic file field on the selected type.
  // Each entry carries `meta: { fieldKey }` so the submit handler knows
  // which field.key each uploaded file belongs to.
  const fileQueue = useUploadQueue();

  // -- Peek reference + live clock ---------------------------------------
  const [previewRef, setPreviewRef] = useState("");
  const [nowTs, setNowTs] = useState(() => new Date());

  const type = useMemo(
    () => (freshTypes || []).find((t) => t.id === typeId),
    [freshTypes, typeId],
  );

  useEffect(() => {
    if (!open) return;
    setTypeId(""); setProjectId(""); setValues({}); fileQueue.reset();
    setCreatingProject(false); setNewProjectName(""); setLocalProjects([]);
    let cancelled = false;
    // Fallback initial : la prop parent (peut être stale).
    setFreshTypes(types || []);
    // Spec iter76 — TOUJOURS refetch les types au moment de l'ouverture.
    // Empêche qu'un cache client obsolète prive un nouveau document d'un
    // champ ajouté récemment à son type.
    setTypesLoading(true);
    api.get("/document-types")
      .then(({ data }) => { if (!cancelled) setFreshTypes(Array.isArray(data) ? data : []); })
      .catch(() => { /* on garde la prop en fallback silencieux */ })
      .finally(() => { if (!cancelled) setTypesLoading(false); });
    api.get("/documents/next-reference")
      .then(({ data }) => { if (!cancelled) setPreviewRef(data.reference || ""); })
      .catch(() => { if (!cancelled) setPreviewRef(""); });
    setNowTs(new Date());
    const id = setInterval(() => setNowTs(new Date()), 1000);
    return () => { cancelled = true; clearInterval(id); };
    // Intentionally exclude `fileQueue` from deps: its identity changes each
    // time an upload entry is added, which used to re-run this effect and
    // wipe `typeId` mid-flow (bug: "back to Sélectionnez un type" after
    // choosing a file). Only re-init when the dialog opens/closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setValues({});
    fileQueue.reset();
    // Same reasoning as above: react only to `typeId`, not to the queue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId]);

  const set = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));

  // Entries filtered per field.key so each file field only sees its own
  // upload card.
  const entriesByField = useMemo(() => {
    const map = {};
    for (const e of fileQueue.entries) {
      const k = e.meta?.fieldKey || "__none__";
      (map[k] = map[k] || []).push(e);
    }
    return map;
  }, [fileQueue.entries]);

  const lotLinked = !!type?.lot_linked; // iter359 pt.7
  // iter417 pt 3 — le type a déjà un champ de lot (obligatoire via lot_linked, ou facultatif dans sa configuration) → pas de section « Lot lié · facultatif » en double
  const hasLotField = lotLinked || (type?.fields || []).some((f) => f.kind === "lot" || /\blot\b/i.test(`${f.label || ""} ${f.key || ""}`));
  const locationRequired = (type?.location_required ?? true) || lotLinked;
  const [lotId, setLotId] = useState("");
  const [lotIds, setLotIds] = useState([]); // iter419 pt 2 — lots liés facultatifs (plusieurs)
  const [lotOpen, setLotOpen] = useState(false); // iter382 pt 4 — liste ouverte → fenêtre élargie à 860 px
  const [projectLots, setProjectLots] = useState([]);
  useEffect(() => { setLotId(""); setLotIds([]); if (projectId) api.get(`/projects/${projectId}/lots`).then(({ data: d }) => setProjectLots(d.items || [])).catch(() => setProjectLots([])); else setProjectLots([]); }, [lotLinked, projectId]); // iter410 L6 — lots chargés pour tout type (lot lié facultatif)
  // iter379 pt 5 — même liste « Lot lié » que Modifier le document (recherche, n° · type · client, « Suggéré » depuis N° LOT) ; reste obligatoire ici.
  const lotNoField = (type?.fields || []).find((f) => /n[°o]?\s*lot|num[ée]ro.*lot|lot.*num/i.test(f.label || "") || /lot/i.test(f.key || ""));
  const lotNoRaw = String(values[lotNoField?.key] ?? "").trim();

  // Split the "Titre" field OUT of the Details grid — it's rendered up top
  // on the same row as the Type selector (spec 2026-02-11, §7bis of
  // /app/memory/project_rules_spec.md). Backend still keeps `titre` in
  // `type.fields`, but the UI hoists it out of that section.
  const titreField = useMemo(() => {
    return (type?.fields || []).find((f) => (f.key || "").toLowerCase() === "titre") || null;
  }, [type]);
  const detailsFields = useMemo(() => {
    return (type?.fields || []).filter((f) => (f.key || "").toLowerCase() !== "titre");
  }, [type]);

  const missing = useMemo(() => {
    if (!type) return [];
    const hasFileForField = (fieldKey) =>
      (entriesByField[fieldKey] || []).some((e) => e.status !== "canceled" && e.status !== "error");
    return (type.fields || [])
      .filter((f) => f.required)
      .filter((f) => {
        if (f.type === "checkbox") return false;
        if (f.type === "file") return !hasFileForField(f.key);
        const v = values[f.key];
        return v === undefined || v === null || v === "";
      })
      .map((f) => ({ key: f.key, label: f.label }));
  }, [type, values, entriesByField]);

  const req = useRequiredFields([
    { key: "type", label: "Type de document", ok: !!typeId },
    { key: "project", label: "Siège / Projet", ok: !typeId || !locationRequired || !!projectId },
    { key: "lot", label: "Lot", ok: !typeId || !lotLinked || !!lotId },
    ...((type?.fields || []).filter((f) => f.required && f.type !== "checkbox").map((f) => ({ key: `field-${f.key}`, label: f.label, ok: !missing.some((m) => m.key === f.key) }))),
  ]);
  const canSubmit = !!typeId && (!locationRequired || !!projectId) && (!lotLinked || !!lotId) && missing.length === 0;
  const resetReq = req.reset;
  useEffect(() => { if (open) resetReq(); }, [open, resetReq]);

  const submit = async () => {
    if (!req.validate()) return;
    setSaving(true);
    try {
      const coerced = { ...values };
      (type.fields || []).forEach((f) => {
        if (f.type === "number" || f.type === "money") {
          if (coerced[f.key] !== undefined && coerced[f.key] !== "") {
            const n = Number(coerced[f.key]);
            if (!Number.isNaN(n)) coerced[f.key] = n;
          }
        }
        if (f.type === "file") delete coerced[f.key];
      });

      const { data } = await api.post("/documents", {
        type_id: typeId,
        project_id: projectId || undefined,
        values: coerced,
        ...(lotId ? { crm_lot_ids: [lotId], crm_sale_id: saleRows.find((r) => r.lot_id === lotId)?.sale_id || undefined } : lotIds.length ? { crm_lot_ids: lotIds, crm_sale_id: saleRows.find((r) => r.lot_id === lotIds[0])?.sale_id || undefined } : crmLink ? { crm_sale_id: crmLink.split(":")[0], crm_lot_ids: [crmLink.split(":")[1]] } : {}), // iter410 L6 — lot lié facultatif
      });

      // Upload every queued file with live progression. Each entry carries a
      // meta.fieldKey so the server knows which dynamic field to patch.
      await fileQueue.uploadAll(async (file, { onProgress, signal, meta }) => {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(
          `/documents/${data.id}/upload-field?field_key=${encodeURIComponent(meta.fieldKey)}`,
          fd,
          {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => onProgress?.((e.loaded / (e.total || file.size)) * 100, e.loaded),
            signal,
          },
        );
      });

      // Reload the final document (values now include the uploaded file
      // descriptors, mutated server-side).
      let latest = data;
      try {
        const { data: fresh } = await api.get(`/documents/${data.id}`);
        latest = fresh;
      } catch { /* keep original */ }

      toast.success("Document créé", { description: latest.reference });
      onOpenChange(false);
      onCreated?.(latest);
      // Spec 2026-02-18 quinquies — stay on the Documents list after
      // creation so users can immediately see the fresh row (and its
      // status/workflow) in context. The old flow used to jump into the
      // document detail page, which was jarring for admins running through
      // multiple creations in a row. Removing the extra `navigate()` keeps
      // us on the list; the parent `Documents.jsx` calls its `load()` from
      // the `onCreated` callback, so the new row appears at the top.
    } catch (err) {
      toast.error("Impossible de créer", { description: err.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const fieldCount = detailsFields.length;         // used for the "Détails · N champs" section counter

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`${lotOpen ? "max-w-[860px]" : "max-w-2xl"} p-0 overflow-hidden gap-0`}
        style={{
          transition: "max-width .2s ease",
          background: "linear-gradient(180deg, #232323, #1B1B1B)",
          border: "1px solid #2E2E2E",
          borderRadius: 16,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
        data-testid="doc-create-dialog"
      >
        {/* -------- Header with 40×40 blue gradient icon ------------------ */}
        <DialogHeader className="px-6 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center shrink-0"
              style={{
                width: 40, height: 40,
                background: `linear-gradient(135deg, ${BLUE}, #1E3E70)`,
                borderRadius: 10,
                boxShadow: "0 4px 12px rgba(43,87,154,0.4)",
              }}
            >
              <FileText size={20} color="#FFFFFF" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle
                className="text-[18px] font-bold leading-tight"
                data-testid="dc-title"
              >
                Nouveau document
              </DialogTitle>
              <div
                className="text-[12px] text-muted-foreground truncate"
                data-testid="dc-subtitle"
              >
                {type?.name || "Sélectionnez un type de document ci-dessous"}
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="px-6 pb-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* -------- Reference pill --------------------------------------- */}
          <div
            className="flex flex-wrap items-center justify-between gap-3"
            style={{
              background: "rgba(43,87,154,0.08)",
              border: "1px solid rgba(43,87,154,0.25)",
              borderRadius: 10,
              padding: "10px 14px",
            }}
            data-testid="dc-readonly-header"
          >
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Référence</span>
              <span
                className="inline-block text-[12px] font-bold px-2.5 py-1 rounded tabular-nums text-white shadow"
                style={{ background: `linear-gradient(135deg, ${BLUE}, #1E3E70)` }}
                data-testid="dc-preview-reference"
              >
                {previewRef || "…"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarIcon size={12} className="text-muted-foreground" />
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Créée le</span>
              <span
                className="text-[12px] font-medium tabular-nums"
                data-testid="dc-preview-now"
              >
                {nowTs.toLocaleString("fr-FR", {
                  day: "2-digit", month: "short", year: "numeric",
                  hour: "2-digit", minute: "2-digit", second: "2-digit",
                })}
              </span>
            </div>
            <div
              className="flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded"
              style={{ color: BLUE_SOFT, background: "rgba(74,140,216,0.10)", border: `1px solid rgba(74,140,216,0.25)` }}
              data-testid="dc-preview-locked"
              title="Le numéro n'est attribué qu'à la création du document"
            >
              Prochain n°
            </div>
          </div>

          {/* -------- Type de document + Titre (ligne d'en-tête) --------- */}
          <div className="grid grid-cols-2 gap-3">
            <div data-field="type">
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1.5">
                Type de document <span style={{ color: "#e24b4a" }}>*</span>
              </label>
              <Select value={typeId} onValueChange={setTypeId}>
                <SelectTrigger
                  className="h-10"
                  style={{ background: "#1A1A1A", border: "1.5px solid rgba(255,255,255,0.08)", borderRadius: 9, ...requiredStyle(req.has("type")) }}
                  data-testid="doc-type-select"
                >
                  <SelectValue placeholder="Choisir un type…" />
                </SelectTrigger>
                <SelectContent>
                  {(freshTypes || []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Titre — hoisted OUT of the Details grid so it always sits on
                the header row next to Type de document. See §7bis of
                /app/memory/project_rules_spec.md. Rendered as a plain input
                (not FieldInput) so it stays disabled until a type is picked. */}
            <div>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1.5">
                Titre {titreField?.required !== false && <span style={{ color: "#e24b4a" }}>*</span>}
              </label>
              {/* Spec rev126 (2026-02-05) — rétablissement de la mise en
                  MAJUSCULES automatique du champ Titre (annule le bypass
                  ajouté en rev118). Deux mécanismes complémentaires :
                    • `textTransform: uppercase` → feedback visuel immédiat
                      (l'utilisateur voit "HELLO" pendant qu'il tape "hello").
                    • Normalisation `toLocaleUpperCase("fr-FR")` à la saisie
                      → la valeur stockée est réellement en majuscules
                        (accents inclus : "é" → "É"). */}
              <Input
                type="text"
                value={values[titreField?.key || "titre"] ?? ""}
                onChange={(e) => set(titreField?.key || "titre", (e.target.value || "").toLocaleUpperCase("fr-FR"))}
                placeholder={typeId ? "Saisir titre…" : "Choisissez d'abord un type de document"}
                disabled={!typeId}
                className="h-10 min-h-10 focus-visible:ring-0 focus-visible:border-[#2B579A]"
                style={{
                  background: "#1A1A1A",
                  border: "1.5px solid rgba(255,255,255,0.08)",
                  borderRadius: 9,
                  opacity: typeId ? 1 : 0.55,
                  textTransform: "uppercase",
                  ...requiredStyle(req.has(`field-${titreField?.key || "titre"}`)),
                }}
                data-testid="doc-field-titre"
                data-no-capitalize
              />
            </div>
          </div>

          {/* Types disponibles counter (moved below to stay clean) */}
          <div
            className="flex items-center gap-1.5 -mt-2"
            style={{ fontSize: 11, color: BLUE_SOFT }}
            data-testid="dc-types-counter"
          >
            <span
              className="inline-flex items-center justify-center shrink-0"
              style={{
                width: 18, height: 18, borderRadius: 5,
                background: "rgba(74,140,216,0.15)",
              }}
            >
              <ClipboardList size={11} />
            </span>
            <span>
              {(freshTypes || []).length} type{(freshTypes || []).length > 1 ? "s" : ""} de document{(freshTypes || []).length > 1 ? "s" : ""} disponible{(freshTypes || []).length > 1 ? "s" : ""}
              {typesLoading && <span className="ml-1 opacity-70">· actualisation…</span>}
            </span>
          </div>

          {/* iter410 L6 — « LOT LIÉ · FACULTATIF » sous Type et Titre, dès que le type est choisi (types à lot obligatoire : rien ne change) */}
          {type && !hasLotField && <OptionalLotSection lots={projectLots} values={lotIds} onChange={setLotIds} projectId={projectId} projectName={(projects || []).find((p) => p.id === projectId)?.name || ""} suggestNo={lotNoRaw} onOpenChange={setLotOpen} testId="doc-optional-lot" />}

          {/* --- Dynamic fields ------------------------------------------- */}
          {type && (detailsFields.length > 0 || locationRequired) && (
            <div>
              <SectionSeparator label={`Détails · ${detailsFields.length + 1} champ${detailsFields.length + 1 > 1 ? "s" : ""}`} />
              <div className="grid grid-cols-2 gap-3">
                {/* Siège / Projet — obligatoire, redevient le premier champ */}
                <div data-field="project">
                  <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1.5 flex items-center gap-1">
                    <MapPin size={11} /> Siège / Projet {locationRequired && <span style={{ color: "#e24b4a" }}>*</span>}
                    {!locationRequired && (
                      <span className="text-[10px] normal-case tracking-normal text-muted-foreground">(optionnelle)</span>
                    )}
                  </label>
                  <div className="flex items-center gap-2">
                    <J3Select projects={allProjects.filter((p) => !lotLinked || p.kind !== "siege")} value={projectId} onChange={setProjectId} testId="doc-project-select" triggerClassName="flex-1" triggerStyle={{ height: 40, background: "#1A1A1A", border: "1.5px solid rgba(255,255,255,0.08)", borderRadius: 9, ...requiredStyle(req.has("project")) }} />
                    {/* Spec rev81 (2026-08-02) — bouton compact "+" pour créer
                        un Siège/Projet inline (admin uniquement). */}
                    {isAdminLevel && (
                      <button
                        type="button"
                        onClick={() => { setCreatingProject((v) => !v); setNewProjectName(""); }}
                        className="flex items-center justify-center rounded-full transition-colors shrink-0"
                        style={{
                          width: 32, height: 32,
                          background: creatingProject ? "rgba(90,158,224,0.22)" : "rgba(90,158,224,0.14)",
                          color: "#5A9EE0",
                          border: "1px solid rgba(90,158,224,0.28)",
                        }}
                        title="Créer un nouveau Siège/Projet"
                        data-testid="doc-project-new-btn"
                      >
                        <Plus size={14} />
                      </button>
                    )}
                  </div>
                  {creatingProject && (
                    <div
                      className="mt-2 rounded-md p-2.5 flex items-center gap-2"
                      style={{ background: "#161616", border: "1px solid #2A2A2A" }}
                      data-testid="doc-project-creator"
                    >
                      <input
                        type="text"
                        autoFocus
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        onKeyDown={async (e) => {
                          if (e.key === "Enter" && newProjectName.trim() && !savingProject) {
                            e.preventDefault();
                            e.currentTarget.blur();
                          } else if (e.key === "Escape") {
                            setCreatingProject(false); setNewProjectName("");
                          }
                        }}
                        placeholder="Nom du Siège/Projet (ex. Résidence Alger, Tour B)"
                        maxLength={80}
                        className="flex-1 h-8 rounded-md px-2 text-sm focus:outline-none focus:ring-1 focus:ring-vertec-blue"
                        style={{ background: "#1B1B1B", border: "1px solid #3A3A3A", color: "#EDEDED" }}
                        data-testid="doc-project-new-name"
                      />
                      <button
                        type="button"
                        onClick={() => { setCreatingProject(false); setNewProjectName(""); }}
                        className="flex items-center justify-center rounded-md hover:bg-white/5"
                        style={{ width: 28, height: 28, color: "#A0A0A0" }}
                        title="Annuler"
                        data-testid="doc-project-new-cancel"
                      >
                        <X size={13} />
                      </button>
                      <button
                        type="button"
                        disabled={!newProjectName.trim() || savingProject}
                        onClick={async () => {
                          setSavingProject(true);
                          try {
                            const { data } = await api.post("/projects", { name: newProjectName.trim() });
                            setLocalProjects((prev) => [...prev, data]);
                            setProjectId(data.id);
                            setCreatingProject(false);
                            setNewProjectName("");
                            toast.success("Siège/Projet créé");
                            if (onProjectCreated) onProjectCreated(data);
                          } catch (err) {
                            toast.error("Impossible de créer", { description: err.response?.data?.detail || "" });
                          } finally {
                            setSavingProject(false);
                          }
                        }}
                        className="flex items-center justify-center rounded-md disabled:opacity-40"
                        style={{ width: 28, height: 28, background: "#2B579A", color: "#FFFFFF" }}
                        title="Créer"
                        data-testid="doc-project-new-save"
                      >
                        <Check size={13} />
                      </button>
                    </div>
                  )}
                </div>
                {lotLinked && (
                  <div data-field="lot" style={{ gridColumn: "1 / -1" }}>
                    <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1.5 flex items-center gap-1"><LinkIcon size={11} /> Lot lié <span style={{ color: "#e24b4a" }}>*</span></label>
                    <div style={{ borderRadius: 9, ...requiredStyle(req.has("lot")) }} data-testid="doc-lot-select">
                      <LotPickerL2 lots={projectLots} value={lotId} onChange={setLotId} suggestNo={lotNoRaw} disabled={!projectId} placeholder={projectId ? "Lot, client, type…" : "Choisir d'abord le projet"} onOpenChange={setLotOpen} testId="doc-lot" />
                    </div>
                  </div>
                )}
                {/* iter417 pt 3 — l'ancien select « Lot / dossier lié (optionnel) » (Ch. 2) faisait doublon avec la section « Lot lié · facultatif » : retiré. */}

                {detailsFields.map((f, idx) => {
                  // Strict 2-col grid — the last field of an odd total spans
                  // both columns to fill the trailing row.
                  const totalFields = 1 + detailsFields.length;   // Siège / Projet + detailsFields
                  const isLast = idx === detailsFields.length - 1;
                  const spanFull = isLast && (totalFields % 2 === 1);
                  return (
                    <FieldInput
                      key={f.id || f.key}
                      field={f}
                      error={req.has(`field-${f.key}`)}
                      spanFull={spanFull}
                      value={values[f.key]}
                      onChange={(v) => set(f.key, v)}
                      documentTypeId={typeId}
                      fileEntries={entriesByField[f.key] || []}
                      onFile={(file) => fileQueue.enqueue(file, { fieldKey: f.key })}
                      onFileCancel={(id) => fileQueue.cancel(id)}
                      onFileRemove={(id) => fileQueue.remove(id)}
                    />
                  );
                })}
              </div>
            </div>
          )}


        </div>

        {req.missing.length > 0 && req.armed && (
          <div className="mx-6 mb-3" style={{ background: "#2a1414", border: "1px solid #e24b4a", borderRadius: 8, padding: "10px 12px" }} data-testid="dc-missing-hint" role="alert">
            <div className="flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 500, color: "#f09595" }}><AlertTriangle size={13} />{req.missing.length} champ{req.missing.length > 1 ? "s" : ""} obligatoire{req.missing.length > 1 ? "s" : ""} à compléter</div>
            <div style={{ fontSize: 12, color: "#e8a0a0", marginTop: 3 }} data-testid="dc-missing-list">{req.missing.map((m) => m.label).join(" · ")}</div>
          </div>
        )}
        <style>{`[data-invalid] > *:not(label) { border: 1px solid #e24b4a !important; border-radius: 9px; }`}</style>
        {/* -------- Footer ------------------------------------------------- */}
        <DialogFooter
          className="px-6 py-4 gap-2"
          style={{ background: "rgba(0,0,0,0.15)", borderTop: "1px solid rgba(255,255,255,0.05)" }}
        >
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-10 border-[rgba(255,255,255,0.12)] hover:bg-white/5"
            data-testid="doc-create-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-incomplete={!canSubmit || undefined}
            className="h-10 text-white border-0 disabled:opacity-50 px-5"
            style={{
              background: `linear-gradient(135deg, ${BLUE}, #1E3E70)`,
              boxShadow: "0 4px 12px rgba(43,87,154,0.4)",
            }}
            data-testid="doc-create-submit"
          >
            {saving ? "Création…" : "Créer le document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
function SectionSeparator({ label }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div
        className="text-[11px] font-semibold uppercase tracking-[0.14em] shrink-0"
        style={{ color: BLUE_SOFT }}
      >
        {label}
      </div>
      <div
        className="h-px flex-1"
        style={{
          background: "linear-gradient(90deg, rgba(74,140,216,0.3), transparent)",
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-type field renderer — dispatches based on `field.type`.
// ---------------------------------------------------------------------------
// All fields must match the height of the "Siège / Projet" SelectTrigger (h-10 =
// 40px) so the dynamic form stays visually uniform, regardless of the type an
// admin picks in the builder. `FIELD_INPUT_CLASS` is applied to every native
// input/select/trigger; wrapper containers (file, checkbox) enforce h-10 as
// well. Never fork this rule per field — the whole point is one canonical
// height across the entire dialog.
const INPUT_STYLE = {
  background: "#1A1A1A",
  border: "1.5px solid rgba(255,255,255,0.08)",
  borderRadius: 9,
};
const FIELD_INPUT_CLASS =
  "h-10 min-h-10 focus-visible:ring-0 focus-visible:border-[#2B579A]";

// ---------------------------------------------------------------------------
// MoneyInput — currency-formatted amount input with a fixed "DA" suffix.
//
// Behaviour (spec 2026-07-17 — fix "Montant DZD accepte les décimales") :
//   • Accepte des montants entiers OU décimaux : "1500", "1500.3", "1500.30".
//   • Séparateur décimal saisissable indifféremment "." OU "," — normalisé
//     en "." dans la valeur transmise (le backend voit toujours un point).
//   • Un seul séparateur décimal max, deux chiffres après max.
//   • Séparateur des milliers = espace insécable fine (U+202F) style français.
//     Affichage : "1 500,30" pour 1500.3, "1 500" pour 1500 (pas de forçage
//     à 2 décimales tant que l'utilisateur tape — la mise en forme finale
//     "1 500,30 DA" est appliquée en lecture seule via `formatMoney`).
//   • Suffixe "DA" fixe à droite, non éditable.
//   • Valeur poussée à onChange = string numérique canonique (ex: "1500.3",
//     "1500", "1500.30"). Elle passe telle quelle à Number() côté submit.
//
// Partagé entre DocumentCreateDialog, DocumentEditDialog et l'input de
// step dans DocumentDetail — exporté explicitement.
// ---------------------------------------------------------------------------
export function MoneyInput({ value, onChange, testid, className, style, placeholder }) {
  // Reformate la valeur brute (avec un "." comme décimale) en chaîne
  // affichable "1 500,3" / "1 500,30" / "1 500". Séparateur de milliers
  // sur la partie entière uniquement, décimales laissées telles quelles.
  const displayed = useMemo(() => {
    if (value === "" || value === null || value === undefined) return "";
    const raw = typeof value === "number" ? String(value) : String(value);
    // Normalise virgule → point puis retire les séparateurs de milliers.
    const canonical = raw.replace(/\s|\u00A0|\u202F/g, "").replace(",", ".");
    if (canonical === "" || canonical === ".") return "";
    const [intPartRaw, decPartRaw] = canonical.split(".");
    const intAsNum = Number(intPartRaw || "0");
    if (Number.isNaN(intAsNum)) return raw;
    const intFmt = intAsNum.toLocaleString("fr-FR"); // U+202F narrow no-break
    if (decPartRaw === undefined) return intFmt;
    // Décimales limitées à 2 chiffres, sans padding automatique — permet à
    // l'utilisateur de taper "1500." puis "3" puis "0" naturellement.
    return `${intFmt},${decPartRaw.slice(0, 2)}`;
  }, [value]);

  const handleChange = (e) => {
    let s = e.target.value || "";
    // Ne retenir que chiffres + séparateurs décimaux + séparateurs de milliers.
    // Puis retirer les séparateurs de milliers et unifier la décimale.
    s = s.replace(/[^\d.,\s\u00A0\u202F]/g, "");
    s = s.replace(/\s|\u00A0|\u202F/g, "");
    // Autoriser une seule décimale : garde la première virgule/point.
    let firstSep = -1;
    let out = "";
    for (const ch of s) {
      if (ch === "," || ch === ".") {
        if (firstSep === -1) {
          firstSep = out.length;
          out += ".";
        }
        // ignore additional separators
      } else {
        out += ch;
      }
    }
    // Limiter à 2 décimales max.
    if (firstSep !== -1) {
      const [intP, decP = ""] = out.split(".");
      out = `${intP}.${decP.slice(0, 2)}`;
      // Supprime le "." final si aucune décimale saisie (pour ne pas polluer)
      // — non, on garde le "." si l'utilisateur vient de le taper, sinon
      // impossible de continuer à saisir des décimales. On laisse tel quel.
    }
    onChange(out);
  };

  return (
    <div className="relative">
      <Input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={displayed}
        onChange={handleChange}
        placeholder={placeholder || "0,00"}
        className={`${className || ""} pr-11`}
        style={style}
        data-testid={testid}
      />
      <span
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-muted-foreground pointer-events-none select-none"
        aria-hidden="true"
      >
        DA
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// formatMoney — helper de lecture seule, à utiliser partout où on affiche
// un montant enregistré. Force 2 décimales (convention comptable DZD) et
// retourne "— DA" pour les valeurs vides. Séparateur de milliers = espace
// insécable fine (U+202F), séparateur décimal = virgule.
//   formatMoney(1500)     → "1 500,00 DA"
//   formatMoney(1500.3)   → "1 500,30 DA"
//   formatMoney(1500.34)  → "1 500,34 DA"
//   formatMoney("1500.3") → "1 500,30 DA"
// ---------------------------------------------------------------------------
export function formatMoney(v) {
  if (v === "" || v === null || v === undefined) return "— DA";
  const raw = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  if (!Number.isFinite(raw)) return `${v} DA`;
  return `${raw.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA`;
}


// ---------------------------------------------------------------------------
// TextMemoryInput — spec iter149 (Feb 2026). Combobox autocomplete alimenté
// par les valeurs déjà utilisées pour ce (documentTypeId, fieldKey). Si la
// saisie ne matche aucune valeur existante, une option « Ajouter "..." »
// apparaît en bas de la liste : sélectionner cette option adopte la nouvelle
// valeur (qui sera mémorisée automatiquement lors du save du document
// — c'est le stockage documents.fields[key] lui-même qui alimente la
// prochaine liste, aucun endpoint dédié « ajouter au dico »).
// ---------------------------------------------------------------------------
const MODULE_COLORS = { documents: "#3568B0", taches: "#D99B1E", validation: "#5B5FC7", accuse: "#F9A826", information: "#2A9A7A", crm: "#2A9A7A", systeme: "#DC2626" };

export function TextMemoryInput({ documentTypeId, fieldKey, fieldLabel, value, onChange, placeholder, testid, className, style, basePath = null, module }) {
  const base = basePath || `/document-types/${documentTypeId}`;
  const scopeId = documentTypeId || basePath;
  const [suggestions, setSuggestions] = useState([]);  // [{value, count}]
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(null);   // string | null
  const [manageOpen, setManageOpen] = useState(false);  // spec iter304 — accès direct « Gérer »

  const reload = () => {
    if (!scopeId || !fieldKey) { setSuggestions([]); return () => {}; }
    let cancelled = false;
    // Spec iter204 (2026-02-28) — bump vers `/field-memory/top` pour
    // récupérer les counts (nombre de documents utilisant chaque
    // valeur). Utile pour la liste permanente sous le champ.
    api
      .get(`${base}/field-memory/top`, { params: { field_key: fieldKey, limit: 500 } })
      .then((r) => { if (!cancelled) setSuggestions(r.data?.top || []); })
      .catch(() => { if (!cancelled) setSuggestions([]); });
    return () => { cancelled = true; };
  };

  useEffect(() => {
    return reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentTypeId, fieldKey]);

  // Spec iter204 (2026-02-28) — Bug fix : refetch au focus du champ.
  // Corrige la « staleness » signalée par les utilisateurs : quand un
  // document A a été enregistré avec « BNA 624 », mais le dialog du
  // document B avait déjà été monté juste avant, sa liste de
  // suggestions restait figée à l'instant du montage. Un refetch au
  // focus garantit que la mémoire est toujours à jour.
  const refreshOnFocus = () => {
    reload();
  };

  const values = suggestions.map((s) => s.value);
  const q = (value || "").trim().toLowerCase();
  // Surlignage de la partie saisie dans chaque suggestion
  const hl = (s) => { const i = q ? s.toLowerCase().indexOf(q) : -1; return i < 0 ? s : <>{s.slice(0, i)}<mark style={{ background: "rgba(42,154,122,.28)", color: "#fff", borderRadius: 2, padding: 0 }}>{s.slice(i, i + q.length)}</mark>{s.slice(i + q.length)}</>; };
  const filtered = q
    ? values.filter((s) => s.toLowerCase().includes(q))
    : values;
  const exactMatch = values.some((s) => s.toLowerCase() === q);
  const showAddOption = !!q && !exactMatch;

  // Spec iter265 point 4 (2026-02) — Suggestion de similarité :
  // si la saisie n'a aucun match exact mais qu'une valeur mémorisée est
  // très proche (distance de Levenshtein normalisée sur les chaînes
  // aplaties casse+espaces), on la propose au clic pour éviter la
  // création silencieuse d'un quasi-doublon (ex : « BNA624 » vs
  // « BNA 624 »). Seuil calibré empiriquement :
  //   • distance ≤ 1 pour les valeurs ≤ 8 caractères
  //   • distance ≤ 2 pour 9–15 caractères
  //   • distance ≤ 3 pour 16+ caractères, tant que d ≤ 20 % de la longueur
  const _flatten = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, "");
  const _lev = (a, b) => {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    let prev = new Array(b.length + 1);
    for (let j = 0; j <= b.length; j++) prev[j] = j;
    for (let i = 1; i <= a.length; i++) {
      const cur = [i];
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      }
      prev = cur;
    }
    return prev[b.length];
  };
  const similarMatch = (() => {
    if (!q || exactMatch) return null;
    const qFlat = _flatten(q);
    if (qFlat.length < 2) return null;
    let best = null;
    let bestD = Infinity;
    for (const v of values) {
      const vFlat = _flatten(v);
      if (!vFlat) continue;
      // Filtre grossier : longueurs très différentes → skip.
      const lenDiff = Math.abs(vFlat.length - qFlat.length);
      const maxLen = Math.max(vFlat.length, qFlat.length);
      let threshold;
      if (maxLen <= 8) threshold = 1;
      else if (maxLen <= 15) threshold = 2;
      else threshold = Math.min(3, Math.floor(maxLen * 0.2));
      if (lenDiff > threshold) continue;
      const d = _lev(qFlat, vFlat);
      // d === 0 signifie identique une fois les espaces/casse aplatis
      // (ex : « BNA624 » vs « BNA 624 »). C'est le cas le plus fort de
      // similarité — on remonte immédiatement.
      if (d === 0) return v;
      if (d <= threshold && d < bestD) {
        best = v;
        bestD = d;
      }
    }
    return best;
  })();

  // Spec iter164E (2026-02-26) — Snap-on-blur : quand l'utilisateur quitte
  // le champ et que sa saisie matche EXACTEMENT (à la casse/espaces près)
  // une valeur déjà mémorisée, on écrit la graphie canonique existante.
  // Empêche la création d'un doublon "bea-alger" vs "BEA-Alger" lors du
  // save du document.
  const snapToCanonical = () => {
    if (!value) return;
    const trimmed = String(value).trim();
    if (!trimmed) return;
    const canonical = values.find((s) => s.toLowerCase() === trimmed.toLowerCase());
    if (canonical && canonical !== value) {
      onChange(canonical);
    } else if (trimmed !== value) {
      onChange(trimmed);
    }
  };

  // Spec iter264 point 3 (2026-02) — Handler du bouton « + ».
  //   • Appelle POST /field-memory/add pour enregistrer immédiatement
  //     la valeur dans la mémoire du champ (via memory_seeded_values).
  //   • Anti-doublon strict (case/space insensitive) côté serveur : si la
  //     valeur existe déjà, retourne added=false sans erreur.
  //   • Toast de confirmation « Ajouté à la mémoire de [label] ».
  //   • Refresh de la liste locale pour afficher immédiatement la valeur.
  const [adding, setAdding] = useState(false);
  const handleAdd = async () => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return;
    const canonical = values.find((s) => s.toLowerCase() === trimmed.toLowerCase());
    if (canonical) {
      onChange(canonical);
      return;
    }
    if (!scopeId || !fieldKey) {
      onChange(trimmed);
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post(
        `${base}/field-memory/add`,
        { field_key: fieldKey, value: trimmed },
      );
      if (data?.added) {
        toast.success(`Ajouté à la mémoire de « ${fieldLabel || fieldKey} »`);
        onChange(data.value || trimmed);
        reload();
      } else {
        // Doublon détecté côté serveur — on adopte la valeur canonique.
        toast.info(`« ${data?.value || trimmed} » est déjà dans la mémoire de « ${fieldLabel || fieldKey} »`);
        onChange(data?.value || trimmed);
        reload();
      }
    } catch (err) {
      toast.error("Impossible d'ajouter à la mémoire", {
        description: err.response?.data?.detail || "",
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="relative" data-testid={`${testid}-memory`}>
      <div className="relative">
        <Input
          data-testid={testid}
          type="text"
          value={value ?? ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => { refreshOnFocus(); setOpen(true); }}
          onBlur={() => { snapToCanonical(); setTimeout(() => setOpen(false), 150); }}
          onKeyDown={(e) => { if (e.key !== "Enter" || !open) return; e.preventDefault(); if (filtered.length) { onChange(filtered[0]); setOpen(false); } else if (showAddOption) { handleAdd(); setOpen(false); } }}
          placeholder={placeholder}
          className={`w-full ${className || ""}`}
          style={{ ...style, paddingRight: showAddOption ? 40 : (style?.paddingRight) }}
          autoComplete="off"
        />
        {/* Spec iter264 point 3 (2026-02) — Bouton « + » vert intégré À
            L'INTÉRIEUR du champ, à droite. Visible uniquement si la
            saisie ne matche aucune valeur existante (anti-doublon strict).
            Style pastille verte (#22C55E) pour signaler l'ajout, dans
            l'esprit demandé par la spec (green "+" affordance). */}
        {showAddOption && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); handleAdd(); }}
            disabled={adding}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-md transition-all disabled:opacity-40"
            style={{
              width: 26, height: 26,
              background: "rgba(34,197,94,0.18)",
              border: "1px solid rgba(34,197,94,0.55)",
              color: "#22C55E",
            }}
            title={`Ajouter « ${(value || "").trim()} » à la mémoire de ${fieldLabel || fieldKey}`}
            data-testid={`${testid}-add-btn`}
            aria-label="Ajouter cette nouvelle valeur à la mémoire"
          >
            <Plus size={14} strokeWidth={2.6} />
          </button>
        )}
      </div>
      {open && (filtered.length > 0 || showAddOption || similarMatch) && (
        <div
          className="absolute left-0 right-0 z-30 mt-1 rounded-md overflow-hidden max-h-56 overflow-y-auto"
          style={{ background: "#1A1A1A", border: "1.5px solid rgba(255,255,255,0.10)", boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}
          data-testid={`${testid}-memory-list`}
        >
          <div className="flex items-center justify-between" style={{ padding: "6px 10px", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Valeurs enregistrées</span>
            <button type="button" onMouseDown={(e) => { e.preventDefault(); setManageOpen(true); setOpen(false); }} className="inline-flex items-center gap-1 text-[10.5px] font-medium rounded px-1.5 py-0.5 hover:bg-white/10" style={{ color: "#7bb3e8" }} title="Gérer les valeurs (renommer, masquer, supprimer)" data-testid={`${testid}-manage-btn`}><Settings2 size={11} /> Gérer</button>
          </div>
          {/* Spec iter265 point 4 (2026-02) — Encart « Vouliez-vous dire »
              affiché en TÊTE du dropdown quand une valeur mémorisée est très
              proche de la saisie (typo, espace en trop). Style ambré discret
              pour signaler « suggestion », sans écraser la vraie liste. */}
          {similarMatch && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(similarMatch); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition"
              style={{
                color: "#F9BC26",
                background: "rgba(249,188,38,0.08)",
                borderBottom: (filtered.length > 0 || showAddOption) ? "1px solid rgba(255,255,255,0.06)" : "none",
              }}
              data-testid={`${testid}-memory-similar`}
              title="Utiliser la valeur existante plutôt que d'en créer une nouvelle"
            >
              <span className="text-[10px] uppercase tracking-widest" style={{ color: "#F9BC26AA" }}>
                Similaire
              </span>
              <span>·</span>
              <span>Vouliez-vous dire «&nbsp;<strong>{similarMatch}</strong>&nbsp;» ?</span>
            </button>
          )}
          {filtered.slice(0, 30).map((s) => (
            <div
              key={s}
              className="group flex items-stretch text-sm hover:bg-white/[0.06] transition"
              data-testid={`${testid}-memory-row`}
            >
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onChange(s); setOpen(false); }}
                className="flex-1 text-left px-3 py-2 truncate"
                style={{ color: "#EDEDED" }}
                data-testid={`${testid}-memory-item`}
              >
                {hl(s)}
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setRenaming(s); setOpen(false); }}
                className="px-2.5 flex items-center opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ color: "#5A9EE0" }}
                title={`Renommer « ${s} » partout`}
                data-testid={`${testid}-memory-rename`}
                aria-label={`Renommer ${s}`}
              >
                <Pencil size={12} strokeWidth={2.2} />
              </button>
            </div>
          ))}
          {showAddOption && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleAdd(); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition"
              style={{
                color: "#22C55E",
                background: (filtered.length > 0 || similarMatch) ? "rgba(34,197,94,0.08)" : "transparent",
                borderTop: (filtered.length > 0 || similarMatch) ? "1px solid rgba(255,255,255,0.06)" : "none",
              }}
              data-testid={`${testid}-memory-add`}
            >
              <Plus size={13} strokeWidth={2.4} /> Ajouter "{(value || "").trim()}" aux valeurs enregistrées
            </button>
          )}
        </div>
      )}
      <ManageMemoryValuesDialog
        open={manageOpen}
        onOpenChange={(v) => { setManageOpen(v); if (!v) reload(); }}
        typeId={documentTypeId}
        basePath={base}
        fieldKey={fieldKey}
        fieldLabel={fieldLabel}
      />
      <RenameMemoryValueDialog
        open={!!renaming}
        onOpenChange={(v) => !v && setRenaming(null)}
        typeId={documentTypeId}
        basePath={base}
        fieldKey={fieldKey}
        fieldLabel={fieldLabel || fieldKey}
        oldValue={renaming || ""}
        existingValues={values}
        onDone={() => {
          reload();
        }}
      />
    </div>
  );
}


export function FieldInput({ field, spanFull, value, onChange, documentTypeId, fileEntries = [], onFile, onFileCancel, onFileRemove, error = false }) {
  const testid = `doc-field-${field.key}`;
  const label = (
    <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1.5">
      {field.label} {field.required && <span style={{ color: "#e24b4a" }}>*</span>}
    </label>
  );
  // Strict 2-col grid — only the last field of an odd total spans both.
  // Ignore the legacy `full_width` flag here so every field lines up neatly.
  const col = spanFull ? "col-span-2" : "";

  const common = { "data-testid": testid };
  const inner = () => {
    switch (field.type) {
      case "money":
        return (
          <MoneyInput
            value={value}
            onChange={onChange}
            testid={testid}
            className={FIELD_INPUT_CLASS}
            style={INPUT_STYLE}
            placeholder="0"
          />
        );
      case "number":
        return (
          <Input
            {...common}
            type="number"
            step="1"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Nombre"
            className={FIELD_INPUT_CLASS}
            style={INPUT_STYLE}
          />
        );
      case "date":
        return (
          <Input
            {...common}
            type="date"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className={FIELD_INPUT_CLASS}
            style={INPUT_STYLE}
          />
        );
      case "dropdown":
        return (
          <Select value={value ?? ""} onValueChange={onChange}>
            <SelectTrigger
              className={FIELD_INPUT_CLASS}
              style={INPUT_STYLE}
              data-testid={testid}
            >
              <SelectValue placeholder="Choisir…" />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "checkbox":
        // Force h-10 wrapper so a checkbox row aligns pixel-perfect with all
        // its siblings in the 2-col grid.
        return (
          <label
            className="h-10 min-h-10 flex items-center gap-2 text-sm cursor-pointer select-none px-3 rounded-[9px]"
            style={INPUT_STYLE}
          >
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              className="h-4 w-4 accent-[#2B579A]"
              data-testid={testid}
            />
            <span className="text-muted-foreground">{field.help_text || "Cocher si applicable"}</span>
          </label>
        );
      case "file":
        // The file field ships its own drop area; wrap it in a container that
        // guarantees min-h-10 so the grid row keeps the canonical baseline
        // even before any file is added.
        // Spec 2026-02-XX rev10 (Feb 2026) — multi-upload activé partout :
        // ``multiple={true}`` sur l'input HTML natif, la file queue stocke
        // et upload chaque fichier séparément. Backend APPEND en liste.
        return (
          <div className="min-h-10">
            <FileOrScanInput
              entries={fileEntries}
              onFiles={(files) => files.forEach((f) => onFile?.(f))}
              onCancel={onFileCancel}
              onRemove={onFileRemove}
              accept="*/*"
              multiple={true}
              testIdPrefix={testid}
            />
          </div>
        );
      case "text_memory":
        return (
          <TextMemoryInput
            documentTypeId={documentTypeId}
            fieldKey={field.key}
            fieldLabel={field.label}
            value={value ?? ""}
            onChange={onChange}
            placeholder={field.help_text || `Saisir ${field.label.toLowerCase()}…`}
            testid={testid}
            className={FIELD_INPUT_CLASS}
            style={INPUT_STYLE}
          />
        );
      case "text":
      default:
        // Convert legacy multi-line Textarea to a single-line Input so text
        // fields sit at exactly 40px like Siège / Projet. If a future spec ever
        // needs a multi-line field, introduce a new `textarea` type — the
        // uniform h-10 rule is deliberate.
        return (
          <Input
            {...common}
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.help_text || `Saisir ${field.label.toLowerCase()}…`}
            className={FIELD_INPUT_CLASS}
            style={INPUT_STYLE}
          />
        );
    }
  };

  return (
    <div className={col} data-field={`field-${field.key}`} data-invalid={error || undefined}>
      {label}
      {inner()}
    </div>
  );
}
